import type { AxiosInstance } from "axios";
import { apiClient } from "@/marketing/infrastructure/http/httpClient";
import type {
  CreateCampaignImagePresignedUrlRequest,
  CreateCampaignImagePresignedUrlResponse,
  CreateInfluencerCampaignRequest,
  InfluencerCampaign,
  InfluencerCampaignFilter,
  InfluencerCampaignStockSourceView,
  ReorderInfluencerCampaignsRequest,
  UpdateInfluencerCampaignRequest,
  UpdateInfluencerCampaignStatusRequest,
} from "@/marketing/models/influencerCampaign";
import { pathSegment } from "@/marketing/lib/pathSegment";

const BASE_PATH = "/influencer/campaigns";
const LIST_PAGE_SIZE = 100;
// 2,000 campaigns; a guard against a server that never returns a short page.
const LIST_ALL_MAX_PAGES = 20;

export class InfluencerCampaignAPIClient {
  constructor(private readonly client: AxiosInstance = apiClient) {}

  public async listInfluencerCampaigns(
    filter: InfluencerCampaignFilter = {},
  ): Promise<InfluencerCampaign[]> {
    const { data } = await this.client.get<InfluencerCampaign[]>(BASE_PATH, {
      params: filter,
    });
    return data;
  }

  /** Reads every page of the list; the facade caps one page at 100 campaigns. */
  public async listAllInfluencerCampaigns(filter: InfluencerCampaignFilter = {}): Promise<InfluencerCampaign[]> {
    const campaigns: InfluencerCampaign[] = [];
    for (let pageIndex = 0; pageIndex < LIST_ALL_MAX_PAGES; pageIndex += 1) {
      const { data } = await this.client.get<InfluencerCampaign[]>(BASE_PATH, {
        params: { ...filter, limit: LIST_PAGE_SIZE, offset: pageIndex * LIST_PAGE_SIZE },
      });
      campaigns.push(...data);
      if (data.length < LIST_PAGE_SIZE) return campaigns;
    }
    throw new Error(`Campaign list has more than ${LIST_ALL_MAX_PAGES * LIST_PAGE_SIZE} campaigns; refusing to show a truncated list`);
  }

  public async getInfluencerCampaign(
    campaignId: string,
  ): Promise<InfluencerCampaign> {
    const { data } = await this.client.get<InfluencerCampaign>(
      `${BASE_PATH}/${pathSegment(campaignId)}`,
    );
    return data;
  }

  /**
   * Where the campaign's goods come from (SSD-7458). Read on its own route, apart from the campaign, so the campaign read
   * never depends on the stock-source column being on the stage.
   */
  public async getInfluencerCampaignStockSource(
    campaignId: string,
  ): Promise<InfluencerCampaignStockSourceView> {
    const { data } = await this.client.get<InfluencerCampaignStockSourceView>(
      `${BASE_PATH}/${pathSegment(campaignId)}/stockSource`,
    );
    return data;
  }

  public async createInfluencerCampaign(
    request: CreateInfluencerCampaignRequest,
  ): Promise<InfluencerCampaign> {
    const { data } = await this.client.post<InfluencerCampaign>(
      BASE_PATH,
      request,
    );
    return data;
  }

  public async updateInfluencerCampaign(
    campaignId: string,
    request: UpdateInfluencerCampaignRequest,
  ): Promise<void> {
    await this.client.patch<void>(`${BASE_PATH}/${pathSegment(campaignId)}`, request);
  }

  public async reorderInfluencerCampaigns(
    orderedCampaignIds: string[],
  ): Promise<void> {
    const request: ReorderInfluencerCampaignsRequest = { orderedCampaignIds };
    await this.client.put<void>(`${BASE_PATH}/order`, request);
  }

  public async updateInfluencerCampaignStatus(
    campaignId: string,
    request: UpdateInfluencerCampaignStatusRequest,
  ): Promise<void> {
    await this.client.patch<void>(`${BASE_PATH}/${pathSegment(campaignId)}/status`, request);
  }

  public async deleteInfluencerCampaign(campaignId: string): Promise<void> {
    await this.client.delete<void>(`${BASE_PATH}/${pathSegment(campaignId)}`);
  }

  public async createCampaignImagePresignedUrl(
    request: CreateCampaignImagePresignedUrlRequest,
  ): Promise<CreateCampaignImagePresignedUrlResponse> {
    const { data } =
      await this.client.post<CreateCampaignImagePresignedUrlResponse>(
        `${BASE_PATH}/presignedUrl`,
        request,
      );
    return data;
  }
}

export const influencerCampaignAPIClient = new InfluencerCampaignAPIClient();
